import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CardActionArea,
  alpha,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getEnabledServices, ServiceConfig } from '../../config/services.config';
import { Lock } from '@mui/icons-material';

interface ServiceGridProps {
  onServiceSelect?: (serviceId: string) => void;
  showDisabled?: boolean;
  userTier?: 'free' | 'premium';
}

const ServiceGrid: React.FC<ServiceGridProps> = ({
  onServiceSelect,
  showDisabled = false,
  userTier = 'free'
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const services = getEnabledServices();
  
  const handleServiceClick = (service: ServiceConfig) => {
    if (service.requiresPremium && userTier === 'free') {
      // Could show upgrade modal here
      return;
    }
    
    if (onServiceSelect) {
      onServiceSelect(service.id);
    } else {
      navigate(service.route);
    }
  };
  
  const getCategoryColor = (category?: string) => {
    switch (category) {
      case 'transport': return theme.palette.primary.main;
      case 'equipment': return theme.palette.warning.main;
      case 'marketplace': return theme.palette.success.main;
      case 'booking': return theme.palette.info.main;
      case 'education': return theme.palette.secondary.main;
      case 'food': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
        },
        gap: 3,
      }}
    >
      {services.map((service) => {
        const Icon = service.icon;
        const isLocked = service.requiresPremium && userTier === 'free';
        
        return (
          <Card
            key={service.id}
            sx={{
              height: '100%',
              opacity: isLocked ? 0.7 : 1,
              transition: 'all 0.3s',
              '&:hover': {
                transform: isLocked ? 'none' : 'translateY(-4px)',
                boxShadow: isLocked ? 1 : 4,
              },
            }}
          >
            <CardActionArea
              onClick={() => handleServiceClick(service)}
              disabled={isLocked}
              sx={{ height: '100%' }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      backgroundColor: alpha(getCategoryColor(service.category), 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                    }}
                  >
                    <Icon 
                      sx={{ 
                        color: getCategoryColor(service.category),
                        fontSize: 28,
                      }} 
                    />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="div">
                      {t(service.name)}
                    </Typography>
                    {service.category && (
                      <Chip
                        label={t(`services.categories.${service.category}`)}
                        size="small"
                        sx={{
                          mt: 0.5,
                          backgroundColor: alpha(getCategoryColor(service.category), 0.1),
                          color: getCategoryColor(service.category),
                        }}
                      />
                    )}
                  </Box>
                  {isLocked && (
                    <Lock sx={{ color: theme.palette.grey[400] }} />
                  )}
                </Box>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ minHeight: 40 }}
                >
                  {t(`services.descriptions.${service.id}`, service.description)}
                </Typography>
                
                {service.requiresPremium && (
                  <Chip
                    label={t('services.premiumOnly')}
                    size="small"
                    color="secondary"
                    sx={{ mt: 1 }}
                  />
                )}
              </CardContent>
            </CardActionArea>
          </Card>
        );
      })}
    </Box>
  );
};

export default ServiceGrid;